// plugins/balloonTipFloating.js
import { computePosition, offset, flip, shift, arrow, autoUpdate } from '@floating-ui/dom';

const BalloonTipFloating = {
  mounted(el, binding) {
    // Conteúdo do tooltip
    const content = typeof binding.value === 'string' ? binding.value : binding.value?.content || '';

    // Opções configuráveis
    const options = typeof binding.value === 'object' ? binding.value : {};
    const placement = options.placement || 'top';
    const showDelay = options.delay || 200;
    const offsetDistance = options.offset || 8;
    const showArrow = options.arrow !== false;
    const trigger = options.trigger || 'hover'; // 'hover', 'click', 'focus'
    const html = options.html || false; // Permitir HTML

    // Criar elemento do tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'balloon-tip-floating';

    // Usar innerHTML ou textContent baseado na opção html
    if (html) {
      tooltip.innerHTML = content;
    } else {
      tooltip.textContent = content;
    }

    tooltip.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      background: #333;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 1.4;
      z-index: 9999;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      max-width: 430px;
      word-wrap: break-word;
    `;

    // Criar seta se habilitada
    let arrowElement = null;
    if (showArrow) {
      arrowElement = document.createElement('div');
      arrowElement.className = 'balloon-tip-arrow';
      arrowElement.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background: #333;
        transform: rotate(45deg);
      `;
      tooltip.appendChild(arrowElement);
    }

    document.body.appendChild(tooltip);

    let cleanup = null;
    let timeoutId = null;

    // Função para mostrar o tooltip
    const show = () => {
      tooltip.style.opacity = '1';

      const middlewares = [
        offset(offsetDistance),
        flip(),
        shift({ padding: 5 })
      ];

      if (showArrow && arrowElement) {
        middlewares.push(arrow({ element: arrowElement }));
      }

      cleanup = autoUpdate(el, tooltip, () => {
        computePosition(el, tooltip, {
          placement: placement,
          middleware: middlewares
        }).then(({ x, y, placement: finalPlacement, middlewareData }) => {
          Object.assign(tooltip.style, {
            left: `${x}px`,
            top: `${y}px`,
          });

          // Posicionar a seta
          if (showArrow && arrowElement && middlewareData.arrow) {
            const { x: arrowX, y: arrowY } = middlewareData.arrow;

            const staticSide = {
              top: 'bottom',
              right: 'left',
              bottom: 'top',
              left: 'right',
            }[finalPlacement.split('-')[0]];

            Object.assign(arrowElement.style, {
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              right: '',
              bottom: '',
              [staticSide]: '-4px',
            });
          }
        });
      });
    };

    // Função para esconder o tooltip
    const hide = () => {
      tooltip.style.opacity = '0';
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };

    // Função com delay
    const showWithDelay = () => {
      timeoutId = setTimeout(show, showDelay);
    };

    const cancelShow = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // Configurar eventos baseado no trigger
    if (trigger === 'hover') {
      el.addEventListener('mouseenter', showWithDelay);
      el.addEventListener('mouseleave', () => {
        cancelShow();
        hide();
      });
    } else if (trigger === 'click') {
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (tooltip.style.opacity === '1') {
          hide();
        } else {
          show();
        }
      });

      // Fechar ao clicar fora
      document.addEventListener('click', hide);
    } else if (trigger === 'focus') {
      el.addEventListener('focus', show);
      el.addEventListener('blur', hide);
    }

    // Guardar referências para cleanup
    el._balloonTip = {
      tooltip,
      arrowElement,
      cleanup,
      show,
      hide,
      timeoutId,
      html
    };
  },

  updated(el, binding) {
    if (el._balloonTip) {
      const content = typeof binding.value === 'string' ? binding.value : binding.value?.content || '';
      const html = typeof binding.value === 'object' ? binding.value.html || false : false;

      // Limpar conteúdo primeiro
      const arrowBackup = el._balloonTip.arrowElement;

      // Atualizar conteúdo com HTML ou texto
      if (html) {
        el._balloonTip.tooltip.innerHTML = content;
      } else {
        el._balloonTip.tooltip.textContent = content;
      }

      // Re-adicionar a seta se existir
      if (arrowBackup) {
        el._balloonTip.tooltip.appendChild(arrowBackup);
      }

      el._balloonTip.html = html;
    }
  },

  unmounted(el) {
    if (el._balloonTip) {
      const { tooltip, cleanup, timeoutId } = el._balloonTip;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (cleanup) {
        cleanup();
      }

      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }

      delete el._balloonTip;
    }
  }
};

export default BalloonTipFloating;
